import React, { useState } from 'react';
import { isKiosk } from '@inter-ikea-storageone/shared/utils/platform';
import { getKioskIntegration } from '@app/utils/integration';
import { isGalleryActive, isUpptackaActive } from '@inter-ikea-storageone/shared/utils/integration';
import {
  KompisLandaHeader,
  KompisMainHeader,
  KompisUpptackaHeader
} from '@inter-ikea-kompis/react-components';
import { useServiceSettings } from '@app/contexts/serviceSettings';
import { getLocale } from '@inter-ikea-storageone/shared/utils/i18nUtils';
import type { LanguageSelectorModalEnum } from '@inter-ikea-kompis/component-language-selector';
import { AnalyticsViewSource } from '@inter-ikea-storageone/shared/analytics';
import { useServices } from '@app/contexts/services/services';
import { getDefaultRedirectUri } from '@app/contexts/services';
interface LandingPageHeaderProps {
  enableSaveToProfile?: boolean;
  experienceGalleryBackURL?: string;
  onOpenDesignClick?: () => void;
}

const LandingPageHeader: React.FC<LandingPageHeaderProps> = ({
  enableSaveToProfile,
  onOpenDesignClick
}) => {
  const kioskIntegration = getKioskIntegration();
  const { user, fetchUserWithLoginAsBackup, logout } = useServices();
  const [visibleModal, setVisibleModal] = useState<
    LanguageSelectorModalEnum.languageSelectorSheet | undefined
  >(undefined);
  const [locale, setLocale] = useState(getLocale());

  const { translations, dexfSettings } = useServiceSettings();

  const onBackClicked = () => {
    if (isGalleryActive() || isUpptackaActive()) {
      kioskIntegration.exitClicked();
      return;
    }
    throw Error('Unable to navigate back as no kiosk platform is present to take over.');
  };

  const onChangeLanguage = (event: CustomEvent) => {
    const newLocale = event.detail.locale;
    setLocale(newLocale);
    const { origin, pathname, search } = window.location;
    const searchParams = new URLSearchParams(search);
    searchParams.set('locale', newLocale);
    window.location.replace(`${origin}${pathname}?${searchParams}`);
  };

  const loginHandler = async () => {
    await fetchUserWithLoginAsBackup(AnalyticsViewSource.hamburgerMenu, {
      state: 'unspecified',
      redirectUri: getDefaultRedirectUri()
    });
  };

  const logoutHandler = async () => {
    await logout(AnalyticsViewSource.hamburgerMenu);
  };

  const toggleModal = (event: CustomEvent) => setVisibleModal(event.detail.visibleModal);

  if (isUpptackaActive()) {
    return (
      <KompisUpptackaHeader
        translations={translations.kompis}
        settings={dexfSettings}
        onBackButtonClick={onBackClicked}
        hideHomeButton
      />
    );
  }

  // In Landa/IPEX Gallery there is a single planner mode where we don't receive the
  // ipexGallery=true parameter. We are OK with the trade-off that the back button in the Kompis Landa header will not do
  // anything in this case. Landa will be decommissioned and it's a smaller amount of them
  // that are running a single planner only. (It's better to present an unusable button than to put
  // login on kiosks.)
  if (isKiosk()) {
    return (
      <KompisLandaHeader
        translations={translations.kompis}
        settings={dexfSettings}
        locale={locale}
        showOpenDesignButton
        showOpenLanguageSelectButton
        visibleModal={visibleModal}
        onModalOpen={toggleModal}
        onModalClose={toggleModal}
        onOpenDesignButtonClick={onOpenDesignClick}
        onBackButtonClick={onBackClicked}
        hideHomeButton
        onLanguageSelectorChange={onChangeLanguage}
      />
    );
  }

  return (
    <KompisMainHeader
      translations={translations.kompis}
      settings={dexfSettings}
      showAccountButton={enableSaveToProfile}
      user={user}
      onLogInButtonClick={loginHandler}
      onLogOutButtonClick={logoutHandler}
    />
  );
};

export default LandingPageHeader;
